// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Unified configuration for all exception types.
 *
 * Provides a single source of truth for exception labels, icons, and colors
 * used throughout the application. This consolidates all exception-related
 * configuration into one place for easy maintenance and consistency.
 */
import { ExceptionType } from '@/constants/asset-exception-type';
import {
  AccessTimeFilled,
  AddShoppingCartRounded,
  Assignment,
  DeleteSweep,
  DoubleArrowOutlined,
  LocalShipping,
  PieChartRounded,
  SellRounded,
  ShoppingCartCheckoutRounded,
  WarningAmber,
} from '@/constants/hardfin-standard-icons';
import { ChipProps } from '@mui/material';
import { ReactElement } from 'react';

/**
 * Configuration for a single exception type.
 * Contains all the properties needed to display and style the exception.
 */
interface ExceptionConfig {
  /**
   * Human-readable label for the exception.
   */
  label: string;

  /**
   * Short guidance describing how the exception can typically be resolved.
   */
  guidance: string;

  /**
   * Icon element to display with the exception chip.
   */
  icon: ReactElement;

  /**
   * Color theme for the exception chip.
   */
  color: ChipProps['color'];
}

/**
 * Unified configuration map for all exception types.
 * Maps exception type keys to their complete configuration.
 */
export const EXCEPTION_CONFIG: Record<ExceptionType | string, ExceptionConfig> =
  {
    // Date inconsistency exceptions
    IN_SERVICE_DATE_BEFORE_INVENTORY: {
      label: 'In-service date before inventory date',
      guidance:
        'Update the in-service or inventory date so the in-service date is on or after the inventory date',
      icon: <DoubleArrowOutlined />,
      color: 'error',
    },
    FIRST_SALE_DATE_BEFORE_INVENTORY: {
      label: 'Sold date before inventory date',
      guidance:
        'Update the sale date or inventory date so the sale date is on or after the inventory date',
      icon: <SellRounded />,
      color: 'error',
    },
    DISPOSAL_DATE_BEFORE_INVENTORY: {
      label: 'Scrapped date before inventory date',
      guidance:
        'Update the disposal date or inventory date so the disposal date is on or after the inventory date',
      icon: <DeleteSweep />,
      color: 'error',
    },
    FIRST_ASSIGNMENT_BEFORE_INVENTORY: {
      label: 'Assignment date before inventory date',
      guidance:
        'Update the assignment start date or inventory date so the assignment start date is on or after the inventory date',
      icon: <Assignment />,
      color: 'error',
    },
    FIRST_MOVEMENT_BEFORE_INVENTORY: {
      label: 'Move date before inventory date',
      guidance:
        'Update the first movement date or inventory date so the first movement date is on or after the inventory date',
      icon: <LocalShipping />,
      color: 'error',
    },

    // Detail inconsistency exceptions
    MISSING_COST_BASIS: {
      label: 'Missing inventory cost basis (for non-scrapped assets)',
      guidance:
        'Add acquisition cost or deployed cost basis in accounting details',
      icon: <AddShoppingCartRounded />,
      color: 'error',
    },
    DEPLOYMENT_WITHOUT_ACQUISITION: {
      label: 'Deployment costs defined without acquisition costs',
      guidance:
        'Add acquisition cost basis or remove deployment-only costs that should not stand alone',
      icon: <ShoppingCartCheckoutRounded />,
      color: 'error',
    },
    SALVAGE_EXCEEDS_50_PERCENT: {
      label: 'Salvage value 50% or more of cost basis',
      guidance:
        'Lower the salvage value below 50% of deployed cost or review the cost basis',
      icon: <PieChartRounded />,
      color: 'error',
    },
    MISSING_USEFUL_LIFE: {
      label: 'Missing useful life (for assets with in-service date)',
      guidance:
        'Set a useful life for the asset so depreciation can be calculated',
      icon: <AccessTimeFilled />,
      color: 'error',
    },
  };

/**
 * Get the configuration for a specific exception type.
 *
 * @param exceptionType - The exception type key
 * @returns The exception configuration, or a default fallback if not found
 */
export function getExceptionConfig(
  exceptionType: ExceptionType | string
): ExceptionConfig {
  return (
    EXCEPTION_CONFIG[exceptionType] ?? {
      label: exceptionType,
      guidance: 'Review the asset data and resolve this exception',
      icon: <WarningAmber fontSize="small" />,
      color: 'error',
    }
  );
}
